import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Coins, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const CreditsSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Optional: You could verify the session with the backend here
    if (!sessionId) {
      // If no session ID, redirect to billing
      setTimeout(() => navigate('/app/billing'), 3000);
    }
    
    // Track credit purchase conversion
    const trackCreditPurchase = async () => {
      try {
        const { trackPurchase } = await import('../lib/metaPixel');
        
        // Get purchase details from URL params if available
        const amount = searchParams.get('amount');
        const credits = searchParams.get('credits');
        
        if (amount) {
          trackPurchase(parseFloat(amount), 'USD', 'credits');
        } else {
          // Default tracking if no amount specified
          trackPurchase(0, 'USD', 'credits');
        }
      } catch (error) {
        console.error('Failed to track credit purchase conversion:', error);
      }
    };
    
    trackCreditPurchase();
  }, [sessionId, navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-gray-1000 dark:text-white mb-3"
          >
            Credits Purchased!
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-gray-950 dark:text-gray-300 mb-8"
          >
            Your credits have been added to your account and are ready to use.
          </motion.p>

          {/* Credits Icon */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-center space-x-3 mb-8 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl"
          >
            <Coins className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <span className="text-lg font-semibold text-black dark:text-white">
              Start creating amazing content!
            </span>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <Button
              onClick={() => navigate('/app/dashboard')}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button
              onClick={() => navigate('/app/billing')}
              variant="outline"
              className="w-full py-3 rounded-xl font-medium dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              View Billing
            </Button>
          </motion.div>

          {/* Session Info (for debugging) */}
          {sessionId && (
            <p className="mt-6 text-xs text-gray-600 dark:text-gray-500">
              Session: {sessionId.substring(0, 20)}...
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CreditsSuccessPage;
